import type { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from "axios"
import axios from "axios"
import Cookies from "js-cookie"
import { baseURL, paramsSerializer } from "@/shared/config/api.ts"
import { Auth, REFERSH_TOKEN_NAME } from "@/shared/lib"

interface IRefreshTokenResponse {
  access: string
}

async function requestConfig(config: InternalAxiosRequestConfig) {
  const token = Auth.getToken()
  if (token != null) config.headers.Authorization = `Bearer ${token}`
  if (!("Content-Type" in config.headers)) config.headers["Content-Type"] = "application/json"
  config.headers.Accept = "*/*"
  const currentLang = localStorage.getItem("i18nextLng") || "en"
  config.headers["accept-language"] = currentLang === "uzKr" ? "uz-cr" : currentLang
  return config
}

function attachInterceptors(instance: AxiosInstance) {
  instance.interceptors.request.use(requestConfig, requestErrorHandler)

  instance.interceptors.response.use(
    response => response,
    async error => {
      const { config, response } = error
      const originalRequest = config as typeof config & { _retry?: boolean }

      if (!response) return Promise.reject(error)
      if (response.status !== 401 || originalRequest._retry) return Promise.reject(error)

      originalRequest._retry = true
      const refreshToken = Cookies.get(REFERSH_TOKEN_NAME)

      if (!refreshToken) {
        Auth.logout()
        return Promise.reject(new Error("No refresh token"))
      }

      try {
        const res = await axios.post<IRefreshTokenResponse>(baseURL + "/auth/api/v1/token/refresh/", { refresh: refreshToken })
        const { access } = res.data
        if (!access) throw new Error("No access token")

        Auth.setToken(access, { expires: 1 })
        originalRequest.headers.Authorization = `Bearer ${access}`
        return instance(originalRequest)
      } catch (err) {
        console.log(err)
        Auth.logout()
        return Promise.reject(err)
      }
    }
  )
}

export async function requestErrorHandler(error: AxiosError): Promise<void> {
  if (error.response !== null) {
    await Promise.reject(error)
  }
  if (error.request !== null) {
    await Promise.reject(error.request)
  }
  await Promise.reject(error)
}

const suffix = "/api/v1"

// Create instances
const commonConfig = { paramsSerializer, timeout: 10000 }

const request = axios.create({
  ...commonConfig,
  baseURL
})
const requestAuth = axios.create({
  ...commonConfig,
  baseURL: `${baseURL}/auth${suffix}`
})
const requestIntensity = axios.create({
  ...commonConfig,
  baseURL: `${baseURL}/intensity${suffix}`
})
const requestSettings = axios.create({ baseURL: `${baseURL}/settings${suffix}`, timeout: 10000 })

attachInterceptors(request)
attachInterceptors(requestIntensity)
attachInterceptors(requestSettings)
attachInterceptors(requestAuth)

export { requestAuth, requestIntensity, requestSettings }
export default request
